import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useBeforeUnload, useBlocker, useLocation } from "react-router-dom";

import {
  AdditionalInterestInput,
  AdditionalInterestType,
} from "@covertree/new-business-graphql";

import { CheckoutContext } from "./checkoutContext";
import { useQuoteDocuments } from "./hooks/useQuoteDocuments";
import type { CheckoutProviderState } from "./types";
import type { PaymentPlanKey } from "../types";
import {
  AdditionalInterestFormData,
  getMaxInterests,
  mapAdditionalInterestToPayload,
  mapLenderToBillingAdditionalInterest,
} from "../AdditionalInterest";
import { Lender } from "@covertree/features";

export interface CheckoutProviderProps
  extends PropsWithChildren,
    Pick<
      CheckoutProviderState,
      | "forceAgentSignResponsibility"
      | "isD2C"
      | "onPaymentSuccess"
      | "policyLocator"
      | "onError"
      | "isInternalAgent"
      | "isAdditionalInterestsEnabled"
      | "monthlyPaymentPlanStates"
    > {
  selectedPaymentPlanKey: PaymentPlanKey;
  initialAdditionalInterests?: AdditionalInterestFormData[];
}

export function CheckoutProvider({
  children,
  forceAgentSignResponsibility,
  isD2C,
  onError,
  onPaymentSuccess,
  policyLocator,
  selectedPaymentPlanKey,
  initialAdditionalInterests,
  isAdditionalInterestsEnabled,
  monthlyPaymentPlanStates,
}: CheckoutProviderProps) {
  const location = useLocation();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [allowNextLocation, setAllowNextLocation] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [additionalInterests, setAdditionalInterests] = useState<
    AdditionalInterestFormData[]
  >(initialAdditionalInterests || []);
  const [lender, setLender] = useState<Lender>();
  const [loanNumber, setLoanNumber] = useState<string>();
  const [isMortgageBillFormValid, setMortgageBillFormValid] = useState(false);

  const hasLienholderError = useMemo(() => {
    const lienholderCount = additionalInterests.filter(
      (interest) => interest.type === AdditionalInterestType.Lienholder
    ).length;
    const maxLienholders = getMaxInterests(selectedPaymentPlanKey)[
      AdditionalInterestType.Lienholder
    ];

    return (
      isAdditionalInterestsEnabled &&
      selectedPaymentPlanKey === "mortgageBill" &&
      lienholderCount > maxLienholders
    );
  }, [
    selectedPaymentPlanKey,
    additionalInterests,
    isAdditionalInterestsEnabled,
  ]);

  const billingAdditionalInterest = useMemo(
    () =>
      selectedPaymentPlanKey === "mortgageBill" && lender && loanNumber
        ? mapLenderToBillingAdditionalInterest(lender, loanNumber)
        : undefined,
    [lender, loanNumber, selectedPaymentPlanKey]
  );

  const mappedAdditionalInterests: AdditionalInterestInput[] = useMemo(() => {
    return [
      ...(billingAdditionalInterest ? [billingAdditionalInterest] : []),
      ...mapAdditionalInterestToPayload(additionalInterests),
    ];
  }, [billingAdditionalInterest, additionalInterests]);

  const {
    documentsLoading,
    binderDocumentLoading,
    documents,
    getQuoteDocuments,
    getBinderDocument,
  } = useQuoteDocuments(
    policyLocator,
    selectedPaymentPlanKey,
    mappedAdditionalInterests
  );

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const { overviewPath, sequoiaPath } = location.state || {};
    const isGoingToAllowedPath =
      nextLocation.pathname.endsWith(overviewPath) ||
      nextLocation.pathname.endsWith(sequoiaPath);

    const isGoingBackAfterManualUWAccepted =
      !isD2C && location.state?.hasManualUWAccepted && !isGoingToAllowedPath;

    const isLoadingAndLeavingCheckout =
      (paymentLoading || documentsLoading || binderDocumentLoading) &&
      currentLocation.pathname !== nextLocation.pathname;

    if (isLoadingAndLeavingCheckout) {
      setShowModal(true);
    }

    return (
      isGoingBackAfterManualUWAccepted ||
      isLoadingAndLeavingCheckout ||
      showModal
    );
  });

  const onPaymentSuccessDelayed = useCallback(
    (isAgentsSignResponsibility?: boolean) => {
      setAllowNextLocation(true);
      onPaymentSuccess(isAgentsSignResponsibility);
    },
    [onPaymentSuccess]
  );

  const onErrorDelayed = useCallback(
    (error?: Error) => {
      setAllowNextLocation(true);
      onError(error);
    },
    [onError]
  );

  const beforeUnloadHandler = useCallback(
    (event: BeforeUnloadEvent) => {
      if (paymentLoading || documentsLoading || binderDocumentLoading) {
        event.preventDefault();
      }

      return paymentLoading || documentsLoading || binderDocumentLoading;
    },
    [paymentLoading, documentsLoading, binderDocumentLoading]
  );

  useBeforeUnload(beforeUnloadHandler);

  useEffect(() => {
    if (blocker.state === "blocked" && allowNextLocation) {
      blocker.proceed();
      setAllowNextLocation(false);
    }
  }, [blocker, allowNextLocation]);

  return (
    <CheckoutContext.Provider
      value={{
        additionalInterests,
        setAdditionalInterests,
        isAdditionalInterestsEnabled,
        hasLienholderError,
        anythingLoading:
          documentsLoading || binderDocumentLoading || paymentLoading,
        showModal,
        setShowModal,
        forceAgentSignResponsibility,
        isD2C,
        documents,
        documentsLoading,
        binderDocumentLoading,
        getQuoteDocuments,
        getBinderDocument,
        onError: onErrorDelayed,
        onPaymentSuccess: onPaymentSuccessDelayed,
        paymentLoading,
        policyLocator,
        setPaymentLoading,
        selectedPaymentPlanKey,
        setLender,
        setLoanNumber,
        monthlyPaymentPlanStates,
        isMortgageBillFormValid,
        setMortgageBillFormValid,
      }}>
      {children}
    </CheckoutContext.Provider>
  );
}
